import React, { Fragment, useEffect, useState } from 'react';
import { Grid, Card, Toolbar, CardContent, FormControlLabel, Dialog, Typography, DialogContent, Button, DialogActions, IconButton, Alert } from '@mui/material';
import ExtendedField from '../../components/text_field';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import axios from 'axios';
import Config from '../../config';
import ExtendedSwitch from '../../components/switch';
import EnhancedTable from '../../components/table';
import DownloadButton from '../../components/download_button';
import Auth from '../../auth';
import Helpers, {validateEmail} from '../../helpers';
import ConfirmDialog from '../../components/confirm_dialog';
import { FieldArray as FieldArrayBase } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import isEqualBase from 'lodash.isequal';
import PapaBase from 'papaparse';
import FileSaverBase from 'filesaver.js-npm';
import InfoIcon from '@mui/icons-material/Info';
import Moment from 'react-moment';
import momentjs from 'moment';
import UserImportDialog from './components/UserImportDialog';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
const FieldArray: any = FieldArrayBase;
// lodash.isequal, papaparse and filesaver.js-npm ship no type declarations
// (same Base-import + any-alias treatment as elsewhere in this migration).
const isEqual: any = isEqualBase;
const Papa: any = PapaBase;
const FileSaver: any = FileSaverBase;

const PermissionNames: Record<string, string> = {
    "users/manage": "User Management",
    "orders/manage": "Order Management",
    "inventory/manage": "Inventory & Product Management",
    "api/manage": "API/Webhook Management",
    "settings/manage": "Settings Management",
    "ccn/manage": "Connected Commerce Network Access",
    "analytics/manage": "Advanced Reporting Access",
    "integrations/manage": "Onboarding Management"
}

const ToolTips: Record<string, React.ReactElement> = {
    "users/manage": <div>
        <Typography>Allows users to:</Typography>
        <ul>
            <li><Typography>Add and manage other users</Typography></li>
            <li><Typography>Set user permissions</Typography></li>
            <li><Typography>View all active users in their company</Typography></li>
            <li><Typography>Export user data</Typography></li>
        </ul>
        </div>,
    "orders/manage": <div>
        <Typography style={{fontStyle: 'italic'}} color='textSecondary'>Enabled by default</Typography>
        <Typography>Allows users to:</Typography>
        <ul>
            <li><Typography>View all orders and related documents</Typography></li>
            <li><Typography>Process orders (accept/cancel, ship, invoice and return orders)</Typography></li>
        </ul>
        </div>,
    "inventory/manage": <div>
        <Typography style={{fontStyle: 'italic'}} color='textSecondary'>Enabled by default</Typography>
        <Typography>Allows users to:</Typography>
        <ul>
            <li><Typography><b>Inventory</b> Update and manage inventory data (SKUs, quantity, costs, etc.)</Typography></li>
            <li><Typography><b>Product Onboarding Center</b> Manage, review and edit products being brought into a retailer's assortment as well as approve or reject products based on the information provided by suppliers</Typography></li>
        </ul>
        </div>,
    "api/manage": <div>
        <Typography>Allows users to:</Typography>
        <ul>
            <li><Typography>View and (re)generate API keys (also needed for accounts using VFTP through EDI)</Typography></li>
            <li><Typography>Create and manage webhooks</Typography></li>
            <li><Typography>Access the API audit log</Typography></li>
        </ul>
        <Typography>This information is sensitive - permissions are generally only given to technical managers, developers or administrators.</Typography>
        </div>,
    "settings/manage": <div>
        <Typography>Allows users to:</Typography>
        <ul>
            <li><Typography><b>Account Information</b> Edit company details (e.g. name, address, logo) from the 'Account Information' page - all users can see this information</Typography></li>
            <li><Typography><b>Product Onboarding Center</b> Create and maintain product taxonomy (categories, attributes, attribute sets) to support vendor compliance</Typography></li>
            <li><Typography><b>Scheduled Reports</b> Add and manage reports sent through email from the 'Scheduled Reports' page </Typography></li>
            <li><Typography><b>Connections</b> View integration details and connection settings from the 'API', 'EDI', or 'Connectors' pages (users without this permission will have the 'Connections' tab omitted from their navigation menu)</Typography></li>
            <li><Typography><b>Scheduled Tasks</b> View task schedules, run tasks manually, and review task history from the 'Scheduled Tasks' page</Typography></li>
            <li><Typography><b>Shipment Options</b> Add and manage shipment options and box configurations from the 'Shipment Options' page</Typography></li>
            <li><Typography><b>Document Settings</b> Configure document settings (payment terms, default addresses, shipment settings, etc.) from the 'Document Settings' page - this page is only available to suppliers</Typography></li>
            <li><Typography><b>Custom Lookups</b> Add and manage records from the 'Custom Lookups' page - this is only available to customers</Typography></li>
        </ul>
        </div>,
    "ccn/manage": <div>
        <Typography>Provides users access to:</Typography>
        <ul>
            <li><Typography>'Discover Partners' page</Typography></li>
        </ul>
        <Typography>This permission is not generally needed - reach out to <a href='mailto:support@logicbroker.com'>support@logicbroker.com</a> if you need access to this page, a Logicbroker subscription may be required.</Typography>
        </div>,
    "analytics/manage": <div>
        <Typography>Provides users access to:</Typography>
        <ul>
            <li><Typography>'Advanced Reporting' page</Typography></li>
        </ul>
        <Typography>Reach out to <a href='mailto:support@logicbroker.com'>support@logicbroker.com</a> if you need access to this page, a Logicbroker subscription may be required.</Typography>
        </div>,
    "integrations/manage": <div>
        <Typography style={{fontStyle: 'italic'}} color='textSecondary'>For retailers only</Typography>
        <Typography>Provides users access to: 'Onboarding' page</Typography>
        <Typography>Allows users to:</Typography>
        <ul>
            <li><Typography>Configure onboarding settings</Typography></li>
            <li><Typography>Invite new suppliers</Typography></li>
            <li><Typography>Manage supplier onboardings</Typography></li>
            <li><Typography>Access On-Demand Onboarding API endpoints</Typography></li>
        </ul>
        </div>
};

type UserManagementProps = {
    setTitle?: (title: string) => void;
};

const isDefaultPerm = (perm: string): boolean => {
    var defPerms = ["orders/manage", "inventory/manage"];
    return defPerms.indexOf(perm) > -1;
};

const propertiesToArray = (obj: any, property: string, prefix: string) => {
    obj[property] = [];
    var keys = Object.keys(obj);
    keys.filter(r => r.indexOf(prefix) === 0).forEach(key => {
        if(obj[key]){
            obj[property].push(key.substring(prefix.length));
        }
        delete obj[key];
    });
};

const UserManagement = (props: UserManagementProps) => {
    const { setTitle } = props;
    // Dynamic per-permission keys (state.newUser["_" + perm], etc.) make a
    // concrete State type more trouble than it's worth here; kept loose like
    // custom_lookups.tsx's equivalent dynamic-key state. A single merge-updater
    // preserves the class's setPartial(partial) shallow-merge semantics.
    const [state, setState] = useState<any>({newUser: {}, showError: false, showInviteSent: false, showEdit: false, showConfirm: false, showToolTip: false, importDialogOpen: false});
    const setPartial = (partial: any) => setState((prev: any) => ({...prev, ...partial}));
    const sendInvite = async (values: any) => {
        var postData: {Email: string; Permissions: string[]} = {
            Email: values.Email,
            Permissions: []
        };
        Object.keys(values).forEach(key => {
            if(key.indexOf("_") === 0 && values[key]){
                postData.Permissions.push(key.substring(1));
            }
        });

        try{
            await axios.post(Config.oauth + '/user/invite', postData);
            setPartial({showInviteSent: true, message: `Sent invitation email to ${postData.Email}.`});
        }catch(e){
            setPartial({showError: true, error: `Failed to send invitation to ${postData.Email}.`});
            throw e;
        }
    }
    useEffect(() => {
        const load = async () => {
            if (setTitle) {
                setTitle('Manage Users');
            }
            var permissions = (await axios.get(Config.oauth + '/user/allpermissions')).data;
            var newUser: Record<string, boolean> = {};
            permissions.forEach((perm: string) => newUser["_" + perm] = isDefaultPerm(perm));
            var profile = (await axios.get(Config.oauth + "/user/userprofile")).data;
            setPartial({permissions: permissions, newUser: newUser, profile});
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const deleteUser = async (user: any) => {
        try{
            var res = (await axios.delete(Config.oauth + '/user/removeuser', { data: user })).data;
            if(res.Errors && Object.keys(res.Errors).length > 0){
                setPartial({showError: true, error: Object.keys(res.Errors).map(r => res.Errors[r].errors.join('\n')).join('\n'), showConfirm: false});
            }else{
                setState((prev: any) => ({...prev, refresh: !prev.refresh, showConfirm: false}));
            }
        }catch(e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            setPartial({showError: true, error: error, showConfirm: false});
        }
    }
    const save = async (user: any) => {
        setPartial({disableEdit: true});
        user = Object.assign({}, user);
        propertiesToArray(user, 'Permissions', '_');
        try{
            var res = (await axios.put(Config.oauth + '/user/userprofile', user)).data;
            if(res.Errors && Object.keys(res.Errors).length > 0){
                setPartial({showError: true, error: Object.keys(res.Errors).map(r => res.Errors[r].errors.join('\n')).join('\n'), disableEdit: false});
            }else{
                setState((prev: any) => ({...prev, refresh: !prev.refresh, showEdit: false}));
            }
        }catch(e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            setPartial({showError: true, error: error, disableEdit: false});
        }
    }
    const exportUsers = async () => {
        let data = (await axios.get(Config.oauth + '/user/users')).data.Data;
        let rows = [['Username', 'Email', 'First Name', 'Last Name', 'Title', 'Permissions', 'Last Login', 'Created']]
        .concat(data.map((r: any) => [r.UserName, r.Email, r.FirstName, r.LastName, r.Title, (r.Permissions || []).sort().join('; '), momentjs(r.LastLogin).toISOString(), momentjs(r.Created).toISOString()]));
        let output = Papa.unparse(rows);
        let blob = new Blob([output], { type: "text/csv" });
        FileSaver.saveAs(blob, 'users.csv');
    }
    const openImportDialog = async () => {
        setPartial({importDialogOpen: true, importLoading: true});
        try {
            var users = (await axios.get(Config.oauth + '/user/users')).data.Data;
            users.forEach((user: any) => {
                user.Permissions = user.Permissions || [];
                user.Groups = user.Groups || [];
            });
            setPartial({importUsers: users, importLoading: false});
        } catch(e) {
            setPartial({showError: true, error: 'Failed to load users. Please try again.', importDialogOpen: false, importLoading: false});
        }
    }
    const showToolTip = (perm: string) => {
        setPartial({showToolTip: true, toolTipPerm: perm})
    }
    let columns: any[] = [
          { id: 'Id', hidden: true},
          { id: 'UserName', filterable: true, sortable: true, label: 'Username' },
          { id: 'Email', filterable: true, sortable: true, label: 'Email' },
          { id: 'FirstName', filterable: true, sortable: true, label: 'First Name', width: '15em' },
          { id: 'LastName', filterable: true, sortable: true, label: 'Last Name', width: '15em' },
          { id: 'Title', filterable: true, sortable: true, label: 'Title',width: '15em' },
          { id: 'LastLogin', filterable: true, sortable: true, label: 'Last Login', width: '13em', template: (val: any) => val ? <Moment format='MM/DD/YYYY hh:mm A'>{val}</Moment> : '' },
          { id: 'Created', filterable: true, sortable: true, label: 'Created', width: '13em', template: (val: any) => val ? <Moment format='MM/DD/YYYY hh:mm A'>{val}</Moment> : '' }
      ];
      let groups: string[] = [];
      let userGroups: string[] = [];
      if(state.user && state.profile){
          userGroups = (state.profile.Groups || []);
          let merged = userGroups.concat(state.user.Groups || []);
          merged = merged.filter((r: string, i: number) => merged.indexOf(r) === i);
          merged = merged.sort();
          groups = merged;
      }
      if(Auth.hasPermission("users/manage")){
          columns.push({ command: 'commands', stopPropagation: 'true', width: '13em', template: (value: any, row: any) => (
              <Fragment>
                <Button size='small' variant='outlined' onClick={() => setPartial({showEdit: true, disableEdit: false, user: row})}>Edit</Button>
                <Button variant='outlined' style={{marginLeft: '0.5rem'}} size='small' disabled={row.InOtherGroups || false} onClick={() => setPartial({showConfirm: true, user: row})}>Delete</Button>
              </Fragment>
          )});
      }
        var config = {
            getData: async function(){
                var data = (await axios.get(Config.oauth + '/user/users')).data.Data;
                data.forEach((user: any) => {
                    user.Permissions.forEach((perm: string) => user["_" + perm] = true);
                    user.Groups = user.Groups || [];
                });
                return data;
            },
            columns: columns,
            actions: Auth.hasPermission("users/manage") && <div style={{marginTop: 'auto', marginBottom: 'auto', display: 'flex', gap: '8px'}}>
              <Button
                size='small'
                variant='outlined'
                onClick={openImportDialog}
              >
                Import
              </Button>
              <DownloadButton size='small' onClick={exportUsers} variant='outlined'>Export</DownloadButton>
              </div>,
          order: 'asc',
          orderBy: 'UserName',
          keyField: 'Id',
          pageSize: 20,
          pageSizes: [10, 20, 50],
          refresh: state.refresh,
          title: 'Users'
        };
        return (
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Card>
                        <Toolbar className='lbtoolbar'>Invite New User</Toolbar>
                        <CardContent>
                            <Form onSubmit={sendInvite}
                            initialValues={state.newUser}
                            render={({ handleSubmit, pristine, invalid, values, form }: any) => (
                              <form autoComplete='off' onSubmit={(e: any) => {handleSubmit(e).then(() => {form.reset()}).catch(() => {})}} style={{display: 'flex', flexDirection: 'column', flex: 1}}>
                              <div style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
                                  <Field style={{width: '100%'}} validate={validateEmail} label='Email' type='email' component={ExtendedField} name='Email' helperText='You may include multiple addresses separated by commas, spaces or semicolons.'/>
                              </div>
                              <div style={{textAlign: 'left', marginTop: '1rem', fontWeight: 'bold'}}>Permissions</div>
                              <div style={{display: 'flex', flex: '1 1 auto', flexDirection: 'column', marginTop: '1rem'}}>
                              {state.permissions && state.permissions.map((perm: string) =>
                                  <div key={"_" + perm} style={{textAlign: 'left'}}>
                                    <FormControlLabel style={{height: '1.5rem', marginBottom: '0.5rem'}} control={<Field type="checkbox" component={ExtendedSwitch} name={"_" + perm}/>} label={PermissionNames[perm] || perm}/>
                                    {ToolTips[perm] ? <IconButton style={{position: 'absolute', marginLeft: '-12px'}} color='primary' onClick={(e: any) => showToolTip(perm)} size="small"><InfoIcon fontSize="inherit" /></IconButton> : ''}
                                  </div>
                              )}
                              </div>
                            <div style={{textAlign: 'left', marginTop: '1em'}}>
                              {/* BUGFIX (found via migration test coverage): sendInvite catches its
                                  own error, records it in state, and re-throws only to satisfy
                                  react-final-form's own submission-error tracking; that rejection
                                  then propagated out of this .then() chain uncaught (a genuine
                                  unhandled-rejection in the browser too, not just under Node's
                                  stricter test default -- confirmed pre-existing via git show
                                  ee28306a). The error is already fully handled/displayed by this
                                  point, so swallow it here. */}
                              <DownloadButton variant='outlined' onClick={(values: any) => handleSubmit(values).then(() => form.reset()).catch(() => {})} type='submit' disabled={pristine || invalid}>Send Invite</DownloadButton>
                            </div>
                              </form>
                        )}/>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={12}>
                    <EnhancedTable config={config}/>
                </Grid>
                <Dialog open={state.showError} onClose={() => {setPartial({showError: false})}}>
                    <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                    <DialogContent><Typography>{state.error}</Typography></DialogContent>
                </Dialog>
                <Dialog open={state.showInviteSent} onClose={() => {setPartial({showInviteSent: false})}}>
                    <Toolbar className='lbtoolbar'>{'Invitation Sent'}</Toolbar>
                    <DialogContent><Typography>{state.message}</Typography></DialogContent>
                </Dialog>
                <Dialog open={state.showToolTip} maxWidth={'md'} onClose={() => {setPartial({showToolTip: false})}}>
                    <Toolbar className='lbtoolbar'>Details for {PermissionNames[state.toolTipPerm] || state.toolTipPerm}</Toolbar>
                    <DialogContent>{ToolTips[state.toolTipPerm] ? ToolTips[state.toolTipPerm] : ''}</DialogContent>
                </Dialog>
                <ConfirmDialog open={state.showConfirm} onClose={() => setPartial({showConfirm: false})} message={`Are you sure you want to delete the user ${state.user ? state.user.UserName : ''}?`} onConfirm={() => deleteUser(state.user)}/>
                  <Dialog
                      maxWidth="sm"
                      fullWidth
                      open={state.showEdit}
                      onClose={(event: any, reason: string) => {
                        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                          if (state.disableEdit) return;
                        }
                        setPartial({showEdit: false});
                      }}>
                      {state.user ?
                      <Form onSubmit={save}
                      mutators={{...arrayMutators}}
                      initialValues={state.user}
                      render={({ handleSubmit, pristine, invalid, values }: any) => (
                        <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                        <Toolbar className='lbtoolbar'>Edit User Information</Toolbar>
                      <DialogContent>
                      <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                        {!(!state.user.InOtherGroups) && <Alert severity="info" style={{ marginBottom: "12px" }}>
                            This user has Group Access across other companies so their permissions cannot be edited.
                        </Alert>}
                        <Field label='Username' disabled component={ExtendedField} name='UserName'/>
                        <Field label='Email' disabled component={ExtendedField} name='Email'/>
                        <Field label='First Name' disabled={state.disableEdit || state.user.InOtherGroups} component={ExtendedField} name='FirstName'/>
                        <Field label='Last Name' disabled={state.disableEdit || state.user.InOtherGroups} component={ExtendedField} name='LastName'/>
                        <Field label='Title' disabled={state.disableEdit || state.user.InOtherGroups} component={ExtendedField} name='Title'/>
                        <div style={{display: 'flex'}}>
                            <div style={{flex: 1}}>
                                <div style={{textAlign: 'left', marginTop: '1rem'}}><Typography variant='h6'>Permissions</Typography></div>
                                    <div style={{display: 'flex', flex: 1, flexDirection: 'column', marginTop: '1rem'}}>
                                    {state.permissions && state.permissions.map((perm: string) =>
                                        <div key={"_" + perm} style={{textAlign: 'left'}}><FormControlLabel control={<Field type="checkbox" disabled={state.disableEdit || state.user.InOtherGroups} component={ExtendedSwitch} name={"_" + perm}/>} label={PermissionNames[perm] || perm}/></div>
                                    )}
                                    </div>
                            </div>
                        {userGroups.length > 0 && <div style={{flex: 1}}>
                                <div style={{textAlign: 'left', marginTop: '1rem'}}><Typography variant='h6'>Group Access</Typography></div>
                                    <div style={{display: 'flex', flex: 1, flexDirection: 'column', marginTop: '1rem'}}>
                                    {groups && <FieldArray name={`Groups`}>
                                        {({fields}: any) => groups.map(key =>
                                            <div key={"G_" + key} style={{textAlign: 'left'}}><FormControlLabel control={<ExtendedSwitch {...({type: "checkbox", disabled: state.disableEdit || state.user.InOtherGroups, input: {checked: fields.value.indexOf(key) > -1, onChange: (e: any) => {fields.value.indexOf(key) === -1 ? fields.push(key) : fields.remove(fields.value.findIndex((r: string) => r === key));} }} as any)}/>} label={key}/></div>
                                        )}
                                    </FieldArray>}
                                </div>
                            </div>}
                        </div>
                        </div>
                      </DialogContent>
                      <DialogActions>
                        <Button type='submit' disabled={isEqual(state.user, values) || invalid || state.disableEdit || state.user.InOtherGroups} color="primary">
                          Save
                        </Button>
                      </DialogActions>
                      </form>
                  )}/> : ''}
                    </Dialog>
                <UserImportDialog
                    open={state.importDialogOpen}
                    onClose={() => setPartial({importDialogOpen: false})}
                    existingUsers={state.importUsers || []}
                    allPermissions={state.permissions || []}
                    loading={state.importLoading}
                    onImportComplete={() => setState((prev: any) => ({...prev, refresh: !prev.refresh}))}
                />
            </Grid>
        );
};

export default UserManagement;
